import React, { useEffect, useState } from 'react';
import useAxios from 'axios-hooks';
import { PrimaryButton, Toggle, Dropdown } from '@fluentui/react';
import { Form, Formik } from 'formik';
import { useParams, useHistory } from 'react-router-dom';
import Select from "react-select";
import TextField from '../../../../Forms/TextField';
import TextCKEditor from '../../../../Forms/TextCKEditor';
import UnitDropdown from '../../../../DomainShared/UnitDropdown';
import Toast from '../../../../Forms/Toast';
import FeatureSummary from '../../../../Util/FeatureSummary';

const newConfig = {
    coBrandID: null,
    coBrandName: '',
    enabled: true,
    navigationLabel: '',
    pageSubheader: '',
    introHeader: '',
    introBody: '',
    contentAreaHeader: '',
    activeSectionLabel: '',
    completedSectionLabel: '',
    emptyStateHeader: '',
    emptyStateBody: '',
    outroHeader: '',
    outroBody: '',
    checklists: [],
    reportingUnitIds: [],
};

const visibilityOptions = [
    { key: 1, text: 'Shown' },
    { key: 2, text: 'Triggered' },
];

// Only surface the character limit once the user actually reaches it, so the
// cap doesn't look like a bug when they can't type any further.
const maxLengthMessage = (value, max) =>
    (value?.length ?? 0) >= max ? `Maximum length reached (${max} characters).` : undefined;

const WizardPage = () => {
    const [showToast, setShowToast] = useState(false);
    const [showError, setShowError] = useState(false);
    const history = useHistory();
    const { cobrandId: idFromParams } = useParams();
    const isEdit = Boolean(idFromParams);

    const [{ data: config }, getConfig] = useAxios(
        { url: `/internal/checklist-dashboard/${idFromParams}`, method: 'GET' },
        { manual: true }
    );

    const [{ data: cobrands }] = useAxios('/internal/cobrands');

    const [{ data: availableChecklists }, getAvailableChecklists] = useAxios(
        { method: 'GET' },
        { manual: true }
    );

    const [{ loading: submitting }, submitForm] = useAxios(
        { url: `/internal/checklist-dashboard/save`, method: 'POST' },
        { manual: true }
    );

    useEffect(() => {
        if (isEdit) {
            getConfig();
            getAvailableChecklists({ url: `/internal/checklist-dashboard/${idFromParams}/available-checklists` });
        }
    }, [idFromParams]);

    const cobrandOptions = cobrands?.map(c => ({ value: c.cobrandId, label: c.name })) || [];
    // label shows both fields since Name (internal) and Header (what members see) aren't
    // required to match, and admins need to know which checklist they're actually picking.
    const checklistOptions = availableChecklists?.map(c => ({
        value: c.checklistID,
        label: `${c.name} (Member sees: "${c.header}")`,
        name: c.name,
        header: c.header,
    })) || [];

    return (
        <Formik
            initialValues={config || newConfig}
            enableReinitialize
            onSubmit={async (values) => {
                setShowError(false);
                const result = await submitForm({ data: values });

                if (!result.isAxiosError) {
                    if (!isEdit) {
                        history.push(`/checklist-feature/checklist-dashboards/${result.data}/wizard`);
                    }
                    setShowToast(true);
                } else {
                    setShowError(true);
                }
            }}
            isInitialValid={false}
        >
            {({ values, errors, setFieldValue, handleSubmit, dirty }) => {
                const isButtonDisabled = submitting || !values.coBrandID;

                const onSelectChecklists = (selected) => {
                    const selectedList = selected || [];
                    const next = selectedList.map((opt, index) => {
                        const existing = values.checklists?.find(c => c.checklistID === opt.value);
                        return {
                            checklistID: opt.value,
                            name: opt.name || opt.label,
                            header: opt.header,
                            orderNumber: existing?.orderNumber ?? index,
                            visibilityType: existing?.visibilityType ?? 1,
                        };
                    });
                    setFieldValue('checklists', next);
                };

                const setVisibility = (checklistID, visibilityType) => {
                    setFieldValue(
                        'checklists',
                        (values.checklists || []).map(c =>
                            c.checklistID === checklistID ? { ...c, visibilityType } : c
                        )
                    );
                };

                const selectedChecklistOptions = checklistOptions.filter(o =>
                    values.checklists?.some(c => c.checklistID === o.value)
                );

                // TextCKEditor locks in whatever value it has on its first render as
                // "initial" and won't pick up later prop changes, so it can't be mounted
                // until the edited dashboard's config has actually loaded.
                const configReady = !isEdit || Boolean(config);

                return (
                    <Form style={{ maxWidth: 600, marginBottom: 500 }}>
                        <div className="displayflex mb20" style={{ justifyContent: 'space-between' }}>
                            <h4>Checklist Dashboard</h4>
                            <PrimaryButton
                                onClick={handleSubmit}
                                text={isEdit ? 'Update' : 'Create'}
                                disabled={isButtonDisabled}
                            />
                        </div>

                        <div className="mb20" style={{ fontStyle: 'italic', color: '#605e5c' }}>
                            Leave any field blank to use its default value.
                        </div>

                        {showError && (
                            <div style={{ color: 'rgb(164, 38, 44)' }}>An error has occurred.</div>
                        )}

                        <div className="panel-h" style={{ fontWeight: 500, marginBottom: 5 }}>Cobrand</div>
                        {isEdit ? (
                            <div style={{ marginBottom: 10, fontWeight: 600 }}>{values.coBrandName}</div>
                        ) : (
                            <Select
                                options={cobrandOptions}
                                placeholder="Select Cobrand"
                                value={cobrandOptions.find(o => o.value === values.coBrandID) || null}
                                onChange={(option) => {
                                    setFieldValue('coBrandID', option.value);
                                    setFieldValue('coBrandName', option.label);
                                    setFieldValue('checklists', []);
                                    setFieldValue('reportingUnitIds', []);
                                    getAvailableChecklists({ url: `/internal/checklist-dashboard/${option.value}/available-checklists` });
                                }}
                                isSearchable
                                styles={{
                                    container: base => ({ ...base, width: 600 }),
                                    control: base => ({ ...base, border: '1px solid #aaaaaa' }),
                                    menuList: base => ({ ...base, maxHeight: 300, overflowY: 'auto' }),
                                }}
                            />
                        )}

                        <Toggle
                            className="mt20"
                            label="Enabled"
                            checked={values.enabled}
                            onText="On"
                            offText="Off"
                            onChange={(e, checked) => setFieldValue('enabled', checked)}
                        />

                        <h4 className="mt20">Navigation &amp; Header</h4>
                        <TextField
                            label="Navigation Label"
                            name="navigationLabel"
                            maxLength={100}
                            description={maxLengthMessage(values.navigationLabel, 100)}
                            value={values.navigationLabel || ''}
                            onChange={(e, v) => setFieldValue('navigationLabel', v)}
                        />
                        <div className="mt10 mb5" style={{ fontWeight: 500 }}>Page Subheader</div>
                        {configReady && (
                            <TextCKEditor
                                value={values.pageSubheader}
                                onChange={(v) => setFieldValue('pageSubheader', v)}
                            />
                        )}

                        <h4 className="mt20">Intro</h4>
                        <TextField
                            label="Intro Header"
                            name="introHeader"
                            maxLength={200}
                            description={maxLengthMessage(values.introHeader, 200)}
                            value={values.introHeader || ''}
                            onChange={(e, v) => setFieldValue('introHeader', v)}
                        />
                        <div className="mt10 mb5" style={{ fontWeight: 500 }}>Intro Body</div>
                        {configReady && (
                            <TextCKEditor
                                value={values.introBody}
                                onChange={(v) => setFieldValue('introBody', v)}
                            />
                        )}

                        <h4 className="mt20">Content Area</h4>
                        <TextField
                            label="Content Area Header"
                            name="contentAreaHeader"
                            maxLength={200}
                            description={maxLengthMessage(values.contentAreaHeader, 200)}
                            value={values.contentAreaHeader || ''}
                            onChange={(e, v) => setFieldValue('contentAreaHeader', v)}
                        />
                        <TextField
                            label="Active Section Label"
                            className="mt10"
                            name="activeSectionLabel"
                            maxLength={100}
                            description={maxLengthMessage(values.activeSectionLabel, 100)}
                            value={values.activeSectionLabel || ''}
                            onChange={(e, v) => setFieldValue('activeSectionLabel', v)}
                        />
                        <TextField
                            label="Completed Section Label"
                            className="mt10"
                            name="completedSectionLabel"
                            maxLength={100}
                            description={maxLengthMessage(values.completedSectionLabel, 100)}
                            value={values.completedSectionLabel || ''}
                            onChange={(e, v) => setFieldValue('completedSectionLabel', v)}
                        />

                        <h4 className="mt20">Empty State</h4>
                        <TextField
                            label="Empty State Header"
                            name="emptyStateHeader"
                            maxLength={200}
                            description={maxLengthMessage(values.emptyStateHeader, 200)}
                            value={values.emptyStateHeader || ''}
                            onChange={(e, v) => setFieldValue('emptyStateHeader', v)}
                        />
                        <TextField
                            label="Empty State Body"
                            className="mt10"
                            name="emptyStateBody"
                            multiline
                            rows={3}
                            value={values.emptyStateBody || ''}
                            onChange={(e, v) => setFieldValue('emptyStateBody', v)}
                        />

                        <h4 className="mt20">Outro</h4>
                        <TextField
                            label="Outro Header"
                            name="outroHeader"
                            maxLength={200}
                            description={maxLengthMessage(values.outroHeader, 200)}
                            value={values.outroHeader || ''}
                            onChange={(e, v) => setFieldValue('outroHeader', v)}
                        />
                        <div className="mt10 mb5" style={{ fontWeight: 500 }}>Outro Body</div>
                        {configReady && (
                            <TextCKEditor
                                value={values.outroBody}
                                onChange={(v) => setFieldValue('outroBody', v)}
                            />
                        )}

                        <h4 className="mt20">Checklists</h4>
                        <Select
                            isMulti
                            options={checklistOptions}
                            value={selectedChecklistOptions}
                            placeholder={values.coBrandID ? 'Select checklists' : 'Select a cobrand first'}
                            isDisabled={!values.coBrandID}
                            onChange={onSelectChecklists}
                            styles={{
                                container: base => ({ ...base, width: 600 }),
                                control: base => ({ ...base, border: '1px solid #aaaaaa' }),
                                menuList: base => ({ ...base, maxHeight: 300, overflowY: 'auto' }),
                            }}
                        />

                        {values.checklists?.length > 0 && (
                            <div className="mt10" style={{ border: '1px solid #ccc', borderRadius: 4 }}>
                                {values.checklists.map((c) => (
                                    <div
                                        key={c.checklistID}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '8px 10px',
                                            borderBottom: '1px solid #eee',
                                        }}
                                    >
                                        <div>
                                            <div style={{ fontSize: 14 }}>{c.name}</div>
                                            {c.header && (
                                                <div style={{ fontSize: 11, color: '#605e5c' }}>Member sees: "{c.header}"</div>
                                            )}
                                        </div>
                                        <Dropdown
                                            selectedKey={c.visibilityType}
                                            options={visibilityOptions}
                                            styles={{ dropdown: { width: 140 } }}
                                            onChange={(e, item) => setVisibility(c.checklistID, item.key)}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        <h4 className="mt20">Reporting Units</h4>
                        <div style={{ fontSize: 12, marginBottom: 5 }}>
                            Leave empty to target ALL units for this cobrand.
                        </div>
                        <UnitDropdown
                            name="reportingUnitIds"
                            cobrandId={values.coBrandID}
                            placeholder={values.coBrandID ? 'Select units (empty = all)' : 'Select a cobrand first'}
                        />

                        <Toast show={showToast} message="Saved!" onClose={() => setShowToast(false)} />
                        <FeatureSummary
                            label="About Checklist Dashboards"
                            summary="Configure the Checklist (Learning Journeys) Dashboard for a cobrand. Enabling this surfaces the dashboard to end users. Choose which of the cobrand's checklists appear and whether each is shown or trigger-based, customize the page copy, and optionally target specific reporting units."
                        />
                    </Form>
                );
            }}
        </Formik>
    );
};

export default WizardPage;
